// Eigentümer-Widgets, Panels und Seitenleiste (Handoff Eigentümer §5, §6.5, §6.6).
// Welche Widgets/Bereiche erscheinen, entscheidet die Rolle (panelSichtbar, lib/eigentuemer/rollen.ts).

import {
  AddressBook, Bank, Buildings, Calculator, CalendarBlank, ClockCounterClockwise, Coins, DeviceMobile, FileText, FolderSimple, Handshake,
  HouseLine, IdentificationBadge, Key, NotePencil, PaperPlaneTilt, PiggyBank, Receipt, SealCheck, Sparkle, SquaresFour, Users, UsersFour,
  UsersThree, Wallet, type Icon,
} from '@phosphor-icons/react';
import { panelSichtbar, type EigGruppe, type EigPanel, type RollenWerte } from '../../lib/eigentuemer/rollen';
import type { WidgetGroesse } from './ui/Widget';
import { PostLogo } from '../epost/PostLogo';

export type EigWidgetGruppe = Exclude<EigGruppe, 'alle'>;
export interface EigWidgetDef { key: EigPanel; gruppe: EigWidgetGruppe; groesse: WidgetGroesse }

/** Reihenfolge und Größen wie im Prototyp (Screens 10–14). */
const ALLE_WIDGETS: readonly EigWidgetDef[] = [
  { key: 'portfolio', gruppe: 'port', groesse: 'gross' },
  { key: 'objekte', gruppe: 'port', groesse: 'breit' },
  { key: 'konto', gruppe: 'fin', groesse: 'breit' },
  { key: 'mieter', gruppe: 'port', groesse: 'normal' },
  { key: 'abrechnung', gruppe: 'fin', groesse: 'normal' },
  { key: 'vertrag', gruppe: 'verw', groesse: 'normal' },
  { key: 'freigaben', gruppe: 'verw', groesse: 'normal' },
  { key: 'steuer', gruppe: 'fin', groesse: 'normal' },
  { key: 'hausgeld', gruppe: 'weg', groesse: 'breit' },
  { key: 'sondereigentum', gruppe: 'weg', groesse: 'normal' },
  { key: 'wegabrechnung', gruppe: 'weg', groesse: 'normal' },
  { key: 'ruecklage', gruppe: 'weg', groesse: 'normal' },
  { key: 'etv', gruppe: 'weg', groesse: 'normal' },
  { key: 'wegverwalter', gruppe: 'weg', groesse: 'normal' },
  { key: 'wegext', gruppe: 'weg', groesse: 'breit' },
  { key: 'bank', gruppe: 'fin', groesse: 'normal' },
  { key: 'beteiligte', gruppe: 'port', groesse: 'normal' },
  { key: 'zustellung', gruppe: 'komm', groesse: 'normal' },
  { key: 'termine', gruppe: 'komm', groesse: 'normal' },
  { key: 'kontakt', gruppe: 'komm', groesse: 'breit' },
  { key: 'portal', gruppe: 'komm', groesse: 'normal' },
  { key: 'kommunikation', gruppe: 'komm', groesse: 'breit' },
  { key: 'dokumente', gruppe: 'komm', groesse: 'normal' },
  { key: 'briefe', gruppe: 'komm', groesse: 'normal' },
  { key: 'notizen', gruppe: 'komm', groesse: 'normal' },
  { key: 'ki', gruppe: 'komm', groesse: 'breit' },
];

type Sichtbarkeit = Pick<RollenWerte, 'verwaltet' | 'hasWEG' | 'extern'>;

/** Widgets dieses Eigentümers (nach Rolle) in einer Gruppe. */
export function eigWidgets(w: Sichtbarkeit, gruppe: EigGruppe): EigWidgetDef[] {
  return ALLE_WIDGETS.filter(d => panelSichtbar(d.key, w) && (gruppe === 'alle' || d.gruppe === gruppe));
}

/** Widget-Titel und Panel-Kopf (Prototyp PT). */
export const EIG_PANEL_KOPF: Record<EigPanel, { widget: string; titel: string; icon: Icon }> = {
  portfolio: { widget: 'Eigentum', titel: 'Eigentum', icon: Buildings },
  objekte: { widget: 'Objekte & Rollen', titel: 'Objekte & Rollen', icon: HouseLine },
  konto: { widget: 'Eigentümerkonto', titel: 'Eigentümerkonto', icon: Wallet },
  mieter: { widget: 'Mieter', titel: 'Mieter', icon: Users },
  abrechnung: { widget: 'Eigentümerabrechnung', titel: 'Eigentümerabrechnung', icon: Receipt },
  vertrag: { widget: 'Verwaltervertrag', titel: 'Verwaltervertrag', icon: Handshake },
  freigaben: { widget: 'Freigaben', titel: 'Freigaben', icon: SealCheck },
  steuer: { widget: 'Steuer', titel: 'Steuer', icon: Calculator },
  hausgeld: { widget: 'Hausgeldkonto', titel: 'Hausgeldkonto', icon: Coins },
  sondereigentum: { widget: 'Sondereigentum', titel: 'Sondereigentum', icon: Key },
  wegabrechnung: { widget: 'WEG-Abrechnung', titel: 'WEG-Abrechnung & Wirtschaftsplan', icon: FileText },
  ruecklage: { widget: 'Erhaltungsrücklage', titel: 'Erhaltungsrücklage', icon: PiggyBank },
  etv: { widget: 'ETV & Beschlüsse', titel: 'ETV & Beschlüsse', icon: UsersThree },
  wegverwalter: { widget: 'Verwalter der WEG', titel: 'Verwalter der WEG', icon: IdentificationBadge },
  wegext: { widget: 'WEG-Verwalter (extern)', titel: 'WEG-Verwalter (extern)', icon: Buildings },
  bank: { widget: 'Bankverbindung', titel: 'Bankverbindung & SEPA', icon: Bank },
  beteiligte: { widget: 'Miteigentümer & Vertreter', titel: 'Miteigentümer & Vertreter', icon: UsersFour },
  zustellung: { widget: 'Zustellung', titel: 'Zustellung & Versandweg', icon: PaperPlaneTilt },
  termine: { widget: 'Termine', titel: 'Termine', icon: CalendarBlank },
  kontakt: { widget: 'Kontakt', titel: 'Stammdaten & Kontakt', icon: AddressBook },
  portal: { widget: 'Eigentümerportal', titel: 'Eigentümerportal', icon: DeviceMobile },
  kommunikation: { widget: 'Kommunikation', titel: 'Kommunikation', icon: ClockCounterClockwise },
  dokumente: { widget: 'Dokumente', titel: 'Dokumente', icon: FolderSimple },
  briefe: { widget: 'Briefe', titel: 'Briefe per Post', icon: PostLogo },
  notizen: { widget: 'Notizen', titel: 'Notizen', icon: NotePencil },
  ki: { widget: 'KI-Zusammenfassung', titel: 'KI-Zusammenfassung', icon: Sparkle },
};

export function istEigPanel(v: string | null): v is EigPanel {
  return v !== null && Object.prototype.hasOwnProperty.call(EIG_PANEL_KOPF, v);
}

export type EigNavKey = EigPanel | 'uebersicht';
export interface EigNavAbschnitt { abschnitt: string; eintraege: { key: EigNavKey; label: string; icon: Icon }[] }

/** Seitenleiste im Eigentümermodus (README §6.6) – Abschnitte nach Rolle. */
export function eigNav(w: Sichtbarkeit & Pick<RollenWerte, 'hasMV' | 'hasSEV'>): EigNavAbschnitt[] {
  const e = (key: EigNavKey, label: string, icon: Icon) => ({ key, label, icon });
  const nav: EigNavAbschnitt[] = [
    { abschnitt: 'Eigentümer', eintraege: [e('uebersicht', 'Übersicht', SquaresFour), e('kontakt', 'Stammdaten', AddressBook), e('portal', 'Eigentümerportal', DeviceMobile)] },
    { abschnitt: 'Portfolio', eintraege: [e('objekte', 'Objekte & Rollen', HouseLine), ...(w.verwaltet ? [e('mieter', 'Mieter', Users)] : [])] },
  ];
  if (w.verwaltet) {
    nav.push({
      abschnitt: w.hasMV && w.hasSEV ? 'Miet- & Sondereigentumsverwaltung' : w.hasMV ? 'Mietverwaltung' : 'Sondereigentumsverwaltung',
      eintraege: [e('vertrag', 'Verwaltervertrag', Handshake), e('konto', 'Eigentümerkonto', Wallet), e('abrechnung', 'Eigentümerabrechnung', Receipt),
        e('freigaben', 'Freigaben', SealCheck), e('steuer', 'Steuer', Calculator)],
    });
  }
  if (w.hasWEG || w.extern) {
    nav.push({
      abschnitt: 'WEG',
      eintraege: [
        ...(w.hasWEG ? [e('hausgeld', 'Hausgeldkonto', Coins), e('sondereigentum', 'Sondereigentum', Key), e('wegabrechnung', 'WEG-Abrechnung', FileText),
          e('ruecklage', 'Erhaltungsrücklage', PiggyBank), e('etv', 'ETV & Beschlüsse', UsersThree), e('wegverwalter', 'Verwalter der WEG', IdentificationBadge),
          ...(w.verwaltet ? [] : [e('steuer', 'Steuer', Calculator)])] : []),
        ...(w.extern ? [e('wegext', 'WEG-Verwalter extern', Buildings)] : []),
      ],
    });
  }
  nav.push({
    abschnitt: 'Allgemein',
    eintraege: [e('beteiligte', 'Miteigentümer & Vertreter', UsersFour), e('zustellung', 'Zustellung', PaperPlaneTilt), e('termine', 'Termine', CalendarBlank),
      e('bank', 'Bankverbindung & SEPA', Bank), e('dokumente', 'Dokumente', FolderSimple), e('briefe', 'Briefe', PostLogo), e('kommunikation', 'Kommunikation', ClockCounterClockwise),
      e('notizen', 'Notizen', NotePencil)],
  });
  return nav;
}
